import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { apiErrorMessage } from "../../lib/apiErrors";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", author: "Kalpavriksha Team", published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api
      .get("/blog?all=true")
      .then((r) => setPosts(r.data))
      .catch((err) => {
        setPosts([]);
        toast.error(apiErrorMessage(err, "Could not load blog posts"));
      });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      cover_image: p.cover_image,
      author: p.author,
      published: p.published,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content || !form.cover_image) {
      toast.error("Title, cover image and content are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/blog/${editing}`, form);
      else await api.post("/blog", form);
      toast.success(editing ? "Post updated" : "Post created");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-4xl">Blog Posts</h1>
          <p className="text-muted-foreground text-sm mt-1">{posts.length} total</p>
        </div>
        <button
          data-testid="new-post-btn"
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <Plus size={18} /> New Post
        </button>
      </div>

      <div className="rounded-2xl border border-border overflow-hidden">
        {posts.map((p) => (
          <div
            key={p.id}
            data-testid="admin-post-row"
            className="flex items-center gap-4 p-4 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors"
          >
            <img src={p.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{p.title}</p>
              <p className="text-sm text-muted-foreground truncate">{p.excerpt}</p>
            </div>
            <span
              className={`text-xs px-2.5 py-1 rounded-full ${
                p.published ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              }`}
            >
              {p.published ? "Published" : "Draft"}
            </span>
            <button
              type="button"
              data-testid="edit-post-btn"
              onClick={() => openEdit(p)}
              className="grid place-items-center w-9 h-9 rounded-full border border-border hover:border-accent transition-colors"
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              data-testid="delete-post-btn"
              onClick={() => remove(p.id)}
              className="grid place-items-center w-9 h-9 rounded-full border border-border hover:border-destructive hover:text-destructive transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setShowForm(false)}>
          <form
            data-testid="post-form"
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-card border border-border p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif-display text-2xl">{editing ? "Edit Post" : "New Post"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="grid place-items-center w-9 h-9 rounded-full border border-border">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                data-testid="post-title"
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                placeholder="Title*"
                className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent"
              />
              <input
                data-testid="post-cover"
                value={form.cover_image}
                onChange={(e) => setForm((s) => ({ ...s, cover_image: e.target.value }))}
                placeholder="Cover image URL*"
                className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent"
              />
              <input
                data-testid="post-excerpt"
                value={form.excerpt}
                onChange={(e) => setForm((s) => ({ ...s, excerpt: e.target.value }))}
                placeholder="Short excerpt"
                className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent"
              />
              <textarea
                data-testid="post-content"
                value={form.content}
                onChange={(e) => setForm((s) => ({ ...s, content: e.target.value }))}
                rows={8}
                placeholder="Content (separate paragraphs with a blank line)*"
                className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent resize-none"
              />
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  data-testid="post-published"
                  checked={form.published}
                  onChange={(e) => setForm((s) => ({ ...s, published: e.target.checked }))}
                />{" "}
                Published
              </label>
              <button
                data-testid="save-post-btn"
                disabled={saving}
                type="submit"
                className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-medium hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Post"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
